import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2, RefreshCw, Check, X, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

// Live camera with the previous photo of the same pose ghosted on top, so
// athletes stand in exactly the same spot every time. Returns a File to the
// caller; the caller still owns the upload.
export default function PoseCamera({ ghostUrl, pose = "front", onCapture, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [ghost, setGhost] = useState(45);          // ghost opacity %
  const [shot, setShot] = useState(null);          // { url, file }

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async (mode) => {
    stop();
    setReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1080 }, height: { ideal: 1440 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch {
      toast.error("Couldn't open the camera — use Upload from library instead.");
      onCancel?.();
    }
  }, [stop, onCancel]);

  useEffect(() => {
    start(facing);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (!blob) { toast.error("Capture failed — try again."); return; }
      const file = new File([blob], `progress-${pose}.jpg`, { type: "image/jpeg" });
      setShot({ url: URL.createObjectURL(blob), file });
      stop();
    }, "image/jpeg", 0.92);
  };

  const retake = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
    start(facing);
  };

  return (
    <div className="space-y-3" data-testid="pose-camera">
      <div className="relative aspect-[3/4] bg-black overflow-hidden border border-white/10">
        {shot ? (
          <img src={shot.url} alt="captured" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <>
            <video ref={videoRef} playsInline muted
              className="absolute inset-0 w-full h-full object-cover" />
            {!ready && (
              <div className="absolute inset-0 grid place-items-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
              </div>
            )}
            {ghostUrl && (
              <img src={ghostUrl} alt="" aria-hidden
                data-testid="pose-camera-ghost"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                style={{ opacity: ghost / 100, filter: "grayscale(1) contrast(1.4)" }} />
            )}
            {/* Centre guides — help line up even with no ghost */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-y-0 left-1/2 w-px bg-[#E2E30A]/30" />
              <div className="absolute inset-x-0 top-1/2 h-px bg-[#E2E30A]/20" />
            </div>
            <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-widest px-2 py-1 bg-black/70 text-white border border-white/15">
              {pose} pose
            </span>
          </>
        )}
      </div>

      {!shot && ghostUrl && (
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Guide</span>
          <input type="range" min="0" max="80" value={ghost} data-testid="pose-camera-ghost-range"
            onChange={(e) => setGhost(Number(e.target.value))}
            className="flex-1 accent-[#E2E30A]" />
          <span className="text-[10px] font-mono text-zinc-400 w-9 text-right">{ghost}%</span>
        </div>
      )}
      <p className="text-[10px] text-zinc-500">
        {ghostUrl
          ? "Line yourself up with the faint outline of your last photo, then shoot."
          : "First photo of this pose — stand square to the camera. Next time you'll get an outline to match."}
      </p>

      {shot ? (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid="pose-camera-retake" onClick={retake}
            className="h-11 border border-white/15 hover:border-white/40 text-white text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
            <RefreshCw className="h-4 w-4" /> Retake
          </button>
          <button type="button" data-testid="pose-camera-use"
            onClick={() => { onCapture?.(shot.file); }}
            className="h-11 bg-[#E2E30A] hover:bg-white text-black text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
            <Check className="h-4 w-4" /> Use photo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_auto_auto] gap-2">
          <button type="button" data-testid="pose-camera-shoot" onClick={capture} disabled={!ready}
            className="h-11 bg-[#E2E30A] hover:bg-white disabled:opacity-60 text-black text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
            <Camera className="h-4 w-4" /> Capture
          </button>
          <button type="button" data-testid="pose-camera-flip"
            onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
            className="h-11 px-3 border border-white/15 hover:border-white/40 text-white inline-flex items-center justify-center" title="Flip camera">
            <SwitchCamera className="h-4 w-4" />
          </button>
          <button type="button" data-testid="pose-camera-cancel" onClick={() => { stop(); onCancel?.(); }}
            className="h-11 px-3 border border-white/15 hover:border-[#FF3B30]/60 text-zinc-300 inline-flex items-center justify-center" title="Cancel">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
